import React, { useState } from 'react';
import { supabase } from '../services/supabase';
import { Mail, ArrowLeft, Check } from 'lucide-react';
import { useI18n } from '../i18n';

const escapeHtml = (text) => text.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

export default function PasswordReset({ onBack }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState(false);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setSuccess(true);
      setMessage(t('passwordReset.linkSent'));
    } catch (error) {
      setMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
        <div className="max-w-md w-full">
          <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200 text-center">
            <div className="bg-verde-100 text-verde-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5">
              <Check size={28} />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {t('passwordReset.successTitle')}
            </h2>
            <p
              className="text-gray-700 mb-6 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: t('passwordReset.successBody', { email: escapeHtml(email) }) }}
            />
            <button
              onClick={onBack}
              className="w-full bg-gray-900 text-white h-10 rounded-lg text-sm font-medium hover:bg-gray-700 flex items-center justify-center gap-2 shadow-sm transition-all"
            >
              <ArrowLeft size={20} />
              {t('passwordReset.backToLogin')}
            </button>
          </div>

          <div className="mt-6 bg-gray-50 border border-gray-200 p-4 rounded-lg text-sm">
            <p className="text-sm text-gray-600">
              {t('passwordReset.tip')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <div className="max-w-md w-full">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-6 font-medium"
        >
          <ArrowLeft size={20} />
          {t('common.back')}
        </button>

        <div className="bg-white p-6 sm:p-8 rounded-xl border border-gray-200">
          <div className="text-center mb-8">
            <div className="inline-flex p-3 rounded-xl bg-lavender-100 text-lavender-600 mb-4">
              <Mail size={28} />
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 mb-1">
              {t('passwordReset.title')}
            </h1>
            <p className="text-sm text-gray-500">
              {t('passwordReset.subtitle')}
            </p>
          </div>

          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">
                {t('passwordReset.emailLabel')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 text-gray-400" size={20} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder')}
                  required
                  className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-lavender-400 focus:border-transparent transition-colors"
                  autoFocus
                />
              </div>
            </div>

            {message && !success && (
              <div className="p-3 rounded-lg text-sm bg-red-50 border border-red-200 text-red-700">
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !email.trim()}
              className="w-full bg-gray-900 text-white h-10 rounded-lg text-sm font-medium hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm transition-all"
            >
              {loading ? t('common.sending') : t('passwordReset.submit')}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-500">
            {t('passwordReset.rememberedPrompt')}{' '}
            <button
              onClick={onBack}
              className="text-lavender-600 hover:text-lavender-700 font-medium underline-offset-2 hover:underline"
            >
              {t('passwordReset.rememberedLink')}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
